import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaGraduationCap,
  FaDollarSign,
  FaSync,
  FaFileImport,
} from "react-icons/fa";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  DashboardGrid,
  DashboardCard,
  CardHeader,
  CardIcon,
  CardValue,
  CardLabel,
  DataTable,
  UserInfo,
  DataTableScroll,
  DashboardGridContainer,
  LoadingContainer,
  TableHeader,
  TableRow,
  TableCell,
  CourseIcon,
  CourseInfo,
  CourseTitle,
  CourseCategory,
  PaginationContainer,
  PageInfo,
  LoadingCell,
  Filters,
  FilterInput,
  CourseStatusBadge,
} from "./AdminCourses.styles";
import { toast } from "sonner";
import adminService from "../../../api/services/admin";
import { getAllCourses } from "../../../store/slices/admin/asyncThunks";
import {
  selectCourses,
  selectCoursesLoading,
  selectCourseFilters,
  selectCoursesPagination,
  selectCoursesTotals,
  setCourseFilters,
} from "../../../store/slices/admin/slice";
import { priceFormatter } from "../../../utils/utils";

const AdminCourses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const courses = useSelector(selectCourses);
  const coursesLoading = useSelector(selectCoursesLoading);
  const courseFilters = useSelector(selectCourseFilters);
  const coursesPagination = useSelector(selectCoursesPagination);
  const coursesTotals = useSelector(selectCoursesTotals);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  const handleSearchChange = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(setCourseFilters({ search: value, page: 1 }));
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Load data on component mount and when filters change
  useEffect(() => {
    dispatch(getAllCourses(courseFilters));
  }, [courseFilters, dispatch]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      await dispatch(getAllCourses(courseFilters)).unwrap();

      toast.success("Podaci su osveženi!");
    } catch (error) {
      toast.error("Greška pri osvežavanju podataka");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.courses || !Array.isArray(data.courses)) {
        toast.error("Neispravan format fajla");
        return;
      }

      const result = await adminService.importCourses(data);
      toast.success(result.message);
      dispatch(getAllCourses(courseFilters));
    } catch (error) {
      toast.error(error.response?.data?.message || "Greška prilikom importa");
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCoursePageChange = (newPage) => {
    dispatch(setCourseFilters({ page: newPage }));
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Upravljanje Kursevima</SectionTitle>
        <SectionActions>
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleImport}
            style={{ display: "none" }}
          />
          <Button
            $secondary
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            $loading={isImporting}
          >
            <FaFileImport />
            {isImporting ? "Importujem..." : "Import"}
          </Button>
          <Button
            $secondary
            onClick={refreshData}
            disabled={isLoading}
            $loading={isLoading}
          >
            <FaSync />
            Osveži
          </Button>
        </SectionActions>
      </SectionHeader>

      <Filters>
        <FilterInput
          placeholder="Pretraži po nazivu ili opisu kursa..."
          defaultValue={courseFilters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Filters>

      <DashboardGridContainer>
        <DashboardGrid>
          <DashboardCard>
            <CardHeader>
              <CardIcon $type="courses">
                <FaGraduationCap />
              </CardIcon>
            </CardHeader>
            <CardValue>
              {coursesLoading ? (
                <l-line-spinner
                  size="24"
                  stroke="2"
                  speed="1"
                  color="var(--accent)"
                />
              ) : (
                coursesTotals?.totalCourses ?? (courses?.length || 0)
              )}
            </CardValue>
            <CardLabel>Ukupno Kurseva</CardLabel>
          </DashboardCard>

          <DashboardCard>
            <CardHeader>
              <CardIcon $type="users">
                <FaUsers />
              </CardIcon>
            </CardHeader>
            <CardValue>
              {coursesLoading ? (
                <l-line-spinner
                  size="24"
                  stroke="2"
                  speed="1"
                  color="var(--accent)"
                />
              ) : (
                coursesTotals?.totalEnrollments ??
                (courses?.reduce(
                  (sum, course) => sum + (course.totalEnrollments || 0),
                  0,
                ) ||
                  0)
              )}
            </CardValue>
            <CardLabel>Ukupno Studenata</CardLabel>
          </DashboardCard>

          <DashboardCard>
            <CardHeader>
              <CardIcon $type="revenue">
                <FaDollarSign />
              </CardIcon>
            </CardHeader>
            <CardValue>
              {coursesLoading ? (
                <l-line-spinner
                  size="24"
                  stroke="2"
                  speed="1"
                  color="var(--accent)"
                />
              ) : (
                priceFormatter(
                  coursesTotals?.totalRevenue ??
                    (courses?.reduce(
                      (sum, course) => sum + (course.revenue || 0),
                      0,
                    ) ||
                      0),
                ) + " RSD"
              )}
            </CardValue>
            <CardLabel>Ukupan Prihod</CardLabel>
          </DashboardCard>
        </DashboardGrid>
      </DashboardGridContainer>

      {coursesLoading ? (
        <LoadingContainer>
          <l-line-spinner
            size="40"
            stroke="3"
            speed="1"
            color="var(--accent)"
          />
        </LoadingContainer>
      ) : (
        <DataTableScroll>
          <DataTable>
            <TableHeader>Kurs</TableHeader>
            <TableHeader>Studenti</TableHeader>
            <TableHeader>Prihod</TableHeader>
            <TableHeader>Cena</TableHeader>
            {courses && courses.length > 0 ? (
              courses.map((course) => (
                <TableRow
                  key={course.id}
                  onClick={() =>
                    navigate(`/admin-panel/kursevi/${course.id}`)
                  }
                >
                  <TableCell>
                    <UserInfo>
                      <CourseIcon>
                        <FaGraduationCap />
                      </CourseIcon>
                      <CourseInfo>
                        <CourseTitle>
                          {course.title}
                          {!course.isActive && (
                            <CourseStatusBadge $active={false}>
                              Neaktivan
                            </CourseStatusBadge>
                          )}
                        </CourseTitle>
                        <CourseCategory>
                          {course.category || "Kategorija"}
                        </CourseCategory>
                      </CourseInfo>
                    </UserInfo>
                  </TableCell>
                  <TableCell>{course.totalEnrollments || 0}</TableCell>
                  <TableCell $hasSpending={parseFloat(course.revenue) > 0}>
                    {priceFormatter(course.revenue || 0)} RSD
                  </TableCell>
                  <TableCell>
                    {priceFormatter(course.discountPrice || course.price)} RSD
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <LoadingCell>
                {courseFilters.search
                  ? "Nema kurseva koji odgovaraju pretrazi"
                  : "Nema dostupnih kurseva"}
              </LoadingCell>
            )}
          </DataTable>
        </DataTableScroll>
      )}

      {coursesPagination && coursesPagination.totalPages > 1 && (
        <PaginationContainer>
          <Button
            $secondary
            disabled={coursesPagination.currentPage === 1}
            onClick={() =>
              handleCoursePageChange(coursesPagination.currentPage - 1)
            }
          >
            Prethodna
          </Button>
          <PageInfo>
            Strana {coursesPagination.currentPage} od{" "}
            {coursesPagination.totalPages}
          </PageInfo>
          <Button
            $secondary
            disabled={
              coursesPagination.currentPage === coursesPagination.totalPages
            }
            onClick={() =>
              handleCoursePageChange(coursesPagination.currentPage + 1)
            }
          >
            Sledeća
          </Button>
        </PaginationContainer>
      )}
    </>
  );
};

export default AdminCourses;
